import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { SlideOver } from "../components/shared/SlideOver";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { FormField } from "../components/shared/FormField";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAdminT } from "../../../hooks/useAdminT";
import { useFormValidation } from "../../../hooks/useFormValidation";
import toast from "react-hot-toast";

interface User {
    id: string;
    name: string;
    email: string;
    role: string;
    active: boolean;
    createdAt: string;
}

export default function UsersPage() {
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const { handleServerErrors } = useFormValidation();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    // Form
    const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<any>();

    // Data Fetching
    const { data: users, isLoading } = useQuery({
        queryKey: ["users"],
        queryFn: () => api.get("/api/admin/users").then(res => res.data.data)
    });

    const { data: roles } = useQuery({
        queryKey: ["roles"],
        queryFn: () => api.get("/api/admin/roles").then(res => res.data.data)
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/users", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            toast.success("User created successfully");
            handleClose();
        },
        onError: (err: any) => handleServerErrors(err, setError, "Failed to create user"),
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/users/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            toast.success("User updated successfully");
            handleClose();
        },
        onError: (err: any) => handleServerErrors(err, setError, "Failed to update user"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            toast.success("User deleted");
            setDeleteTarget(null);
        },
        onError: () => {
            toast.error("Failed to delete user");
            setDeleteTarget(null);
        }
    });

    // Handlers
    const handleClose = () => {
        setIsDrawOpen(false);
        setEditingId(null);
        reset();
    };

    const handleEdit = (user: User) => {
        setEditingId(user.id);
        reset({ name: user.name, email: user.email, roleId: roles?.find((r: any) => r.name === user.role)?.id, active: user.active });
        setIsDrawOpen(true);
    };

    const onSubmit = (data: any) => {
        if (editingId) {
            updateMutation.mutate(data);
        } else {
            createMutation.mutate({ ...data, password: "password123" }); // Default password
        }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("users.title")}
                description={t("users.subtitle")}
                actions={[
                    {
                        label: t("users.add"),
                        icon: UserPlus,
                        onClick: () => setIsDrawOpen(true),
                        variant: "primary"
                    }
                ]}
            />

            <AdminTable
                data={users}
                isLoading={isLoading}
                columns={[
                    { header: t("users.fields.name"), accessorKey: "name", className: "font-medium" },
                    { header: t("users.fields.email"), accessorKey: "email" },
                    { header: t("users.fields.role"), cell: (u) => <span className="bg-slate-100 px-2 py-1 rounded text-xs font-semibold">{u.role}</span> },
                    { header: t("common.status"), cell: (u) => <StatusBadge status={u.active} /> },
                    { header: t("common.createdAt"), cell: (u) => new Date(u.createdAt).toLocaleDateString() },
                ]}
                onEdit={handleEdit}
                onDelete={(u) => setDeleteTarget(u)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("users.delete") || "Delete User"}
                message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("users.edit") : t("users.add")}
                description={editingId ? t("users.editDesc") : t("users.addDesc")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                            {editingId ? t("common.save") : t("common.create")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6">
                    <FormField label={t("users.fields.name")} error={errors.name?.message as string} required>
                        <input {...register("name", { required: "Name is required", minLength: { value: 2, message: "Name must be at least 2 characters" } })} className="w-full px-3 py-2 border rounded-md" />
                    </FormField>
                    <FormField label={t("users.fields.email")} error={errors.email?.message as string} required>
                        <input type="email" {...register("email", { required: "Email is required", pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: "Please enter a valid email" } })} className="w-full px-3 py-2 border rounded-md" />
                    </FormField>
                    <FormField label={t("users.fields.role")} error={errors.roleId?.message as string} required>
                        <select {...register("roleId", { required: "Role is required" })} className="w-full px-3 py-2 border rounded-md">
                            <option value="">{t("roles.title")}</option>
                            {roles?.map((r: any) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                    </FormField>
                    {editingId && (
                        <div className="flex items-center gap-2">
                            <input type="checkbox" {...register("active")} id="active" />
                            <label htmlFor="active" className="text-sm font-medium text-slate-700">{t("users.fields.active")}</label>
                        </div>
                    )}
                </form>
            </SlideOver>
        </div>
    );
}
